import React, { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Plus, X, Trash2, Save, Megaphone, Upload as UploadIcon, Eye, Popcorn, Sparkles, Mail } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  title: "", subtitle: "", cta: "", media_url: "",
  background_color: "#0A0A0A", accent_color: "#D4AF37",
  duration_sec: 5, enabled: true,
};

const PRESETS = [
  { l: "Kino-Schwarz + Gold", bg: "#0A0A0A", ac: "#D4AF37" },
  { l: "Snackbar-Braun", bg: "#1A0F00", ac: "#EEDC82" },
  { l: "Velvet Rot", bg: "#3B0C10", ac: "#EE9CA1" },
  { l: "Marvel", bg: "#040A2E", ac: "#EE1C24" },
  { l: "Weihnachten", bg: "#0D1F0D", ac: "#D4AF37" },
];

export default function PrerollAds() {
  const [ads, setAds] = useState([]);
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/preroll-ads"); setAds(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (a) => {
    if (!window.confirm(`Werbespot „${a.title}" löschen?`)) return;
    try { await api.delete(`/preroll-ads/${a.id}`); toast.success("Gelöscht"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggle = async (a) => {
    try { await api.patch(`/preroll-ads/${a.id}`, { ...a, enabled: !a.enabled }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="preroll-ads-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#A8A398]">Kino-Vorspann</div>
          <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl flex items-center gap-3"><Megaphone size={30} className="text-[#D4AF37]" /> Werbespots</h1>
        </div>
        <button data-testid="ad-new-btn" onClick={() => setEditing({ ...EMPTY })}
          className="bg-[#D4AF37] text-black px-4 py-2 rounded-md text-sm uppercase tracking-widest hover:bg-[#EEDC82] flex items-center gap-2 transitions-safe">
          <Plus size={14} /> Neuer Spot
        </button>
      </div>
      <div className="text-xs text-[#A8A398] max-w-3xl leading-relaxed">
        Diese Werbespots werden zwischen den Trailern im Kino-Vorspann angezeigt. Wenn du keine eigenen anlegst, laufen automatisch 3 Standard-Slides (Snackbar, Club, Newsletter).
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {ads.map((a) => (
          <div key={a.id} className={`card-lux p-4 relative overflow-hidden ${a.enabled ? "" : "opacity-50"}`} data-testid={`ad-card-${a.id}`}>
            <AdPreviewMini item={a} />
            <div className="mt-3 flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-heading text-xl truncate">{a.title || "—"}</div>
                <div className="text-xs text-[#A8A398] truncate">{a.subtitle || ""}</div>
                <div className="text-[10px] text-[#575148] mt-1">{a.duration_sec} s · {a.cta}</div>
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                <button onClick={() => setPreview(a)} className="text-xs px-2 py-1 border border-[#2A2A2A] rounded hover:border-[#D4AF37] flex items-center gap-1"><Eye size={12} /></button>
                <button onClick={() => setEditing(a)} data-testid={`ad-edit-${a.id}`} className="text-xs px-2 py-1 border border-[#D4AF37] text-[#D4AF37] rounded">Bearbeiten</button>
                <button onClick={() => toggle(a)} className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border ${a.enabled ? "border-[#D4AF37] text-[#D4AF37]" : "border-[#5B1319] text-[#EE9CA1]"}`}>
                  {a.enabled ? "aktiv" : "aus"}
                </button>
                <button onClick={() => remove(a)} className="text-xs px-2 py-1 border border-[#2A2A2A] rounded hover:border-[#7A1921]"><Trash2 size={12} /></button>
              </div>
            </div>
          </div>
        ))}
        {!ads.length && (
          <div className="col-span-full text-center text-[#A8A398] py-16 border border-dashed border-[#2A2A2A] rounded-lg">
            Noch keine eigenen Werbespots. Klicke &bdquo;Neuer Spot&ldquo; &mdash; es laufen bis dahin drei Standard-Slides.
          </div>
        )}
      </div>

      {editing && <AdEditor initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {preview && <FullPreview item={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}

function AdPreviewMini({ item }) {
  return (
    <div className="relative aspect-video rounded-md overflow-hidden" style={{ background: item.background_color || "#0A0A0A" }}>
      {item.media_url && <img src={item.media_url} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} className="absolute inset-0 w-full h-full object-cover opacity-40" />}
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-3">
        <div className="text-[7px] uppercase tracking-[0.4em] mb-1" style={{ color: item.accent_color || "#D4AF37" }}>· Werbung ·</div>
        <div className="font-heading text-lg leading-tight" style={{ color: item.accent_color || "#D4AF37" }}>{item.title || "…"}</div>
        {item.subtitle && <div className="text-[9px] text-[#F5F0E6]/80 mt-1 line-clamp-2">{item.subtitle}</div>}
        {item.cta && <div className="mt-1 text-[8px] uppercase tracking-widest border rounded-full px-2 py-0.5" style={{ borderColor: item.accent_color, color: item.accent_color }}>{item.cta}</div>}
      </div>
    </div>
  );
}

function AdEditor({ initial, onClose, onSaved }) {
  const [form, setForm] = useState({ ...EMPTY, ...initial });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const upload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      upd("media_url", data.url);
      toast.success("Bild hochgeladen");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Titel fehlt");
    setBusy(true);
    try {
      const payload = { ...form, duration_sec: Number(form.duration_sec) || 5 };
      if (initial.id) await api.patch(`/preroll-ads/${initial.id}`, payload);
      else await api.post(`/preroll-ads`, payload);
      toast.success("Werbespot gespeichert");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm p-3 overflow-y-auto flex items-start justify-center">
      <div className="w-full max-w-4xl bg-[#0A0A0A] border border-[#2A2A2A] rounded-xl my-4" data-testid="ad-editor">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#1A1A1A]">
          <div className="font-heading text-2xl">{initial.id ? "Werbespot bearbeiten" : "Neuer Werbespot"}</div>
          <button onClick={onClose}><X /></button>
        </div>
        <div className="p-6 grid lg:grid-cols-[minmax(0,340px)_1fr] gap-6">
          <div className="space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Titel</label>
              <input data-testid="ad-title" value={form.title} onChange={(e) => upd("title", e.target.value)} placeholder="z. B. Snackbar geöffnet"
                className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Untertitel</label>
              <textarea rows={2} value={form.subtitle} onChange={(e) => upd("subtitle", e.target.value)} placeholder="Popcorn · Nachos · Getränke"
                className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Call-to-Action</label>
              <input value={form.cta} onChange={(e) => upd("cta", e.target.value)} placeholder="Jetzt an der Snackbar"
                className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Dauer (Sekunden)</label>
              <input type="number" min={2} max={30} value={form.duration_sec} onChange={(e) => upd("duration_sec", parseInt(e.target.value || 5))}
                className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none font-mono" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Hintergrundfarbe</label>
                <div className="flex gap-2 items-center mt-1">
                  <input type="color" value={form.background_color} onChange={(e) => upd("background_color", e.target.value)}
                    className="w-10 h-10 rounded cursor-pointer bg-transparent border border-[#2A2A2A]" />
                  <input value={form.background_color} onChange={(e) => upd("background_color", e.target.value)}
                    className="flex-1 bg-[#050505] border border-[#2A2A2A] rounded px-2 py-1 text-xs font-mono outline-none focus:border-[#D4AF37]" />
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Akzentfarbe</label>
                <div className="flex gap-2 items-center mt-1">
                  <input type="color" value={form.accent_color} onChange={(e) => upd("accent_color", e.target.value)}
                    className="w-10 h-10 rounded cursor-pointer bg-transparent border border-[#2A2A2A]" />
                  <input value={form.accent_color} onChange={(e) => upd("accent_color", e.target.value)}
                    className="flex-1 bg-[#050505] border border-[#2A2A2A] rounded px-2 py-1 text-xs font-mono outline-none focus:border-[#D4AF37]" />
                </div>
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#A8A398] mb-1">Presets</div>
              <div className="flex flex-wrap gap-1">
                {PRESETS.map((p) => (
                  <button key={p.l} onClick={() => setForm((f) => ({ ...f, background_color: p.bg, accent_color: p.ac }))}
                    className="text-[10px] uppercase tracking-widest border border-[#2A2A2A] rounded px-2 py-1 hover:border-[#D4AF37] transitions-safe">
                    <span className="inline-block w-2 h-2 rounded-full mr-1 align-middle" style={{ background: p.ac }} />
                    {p.l}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Hintergrundbild (optional)</label>
              <div className="mt-1 flex items-center gap-2">
                <input value={form.media_url} onChange={(e) => upd("media_url", e.target.value)} placeholder="URL oder hochladen"
                  className="flex-1 bg-[#050505] border border-[#2A2A2A] rounded px-2 py-1 text-xs outline-none focus:border-[#D4AF37]" />
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
                <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="ad-upload-btn"
                  className="text-xs border border-[#2A2A2A] rounded px-3 py-1 text-[#A8A398] hover:border-[#D4AF37] hover:text-[#D4AF37] flex items-center gap-1">
                  <UploadIcon size={12} /> {uploading ? "…" : "Upload"}
                </button>
              </div>
            </div>

            <label className="text-sm flex items-center gap-2 text-[#A8A398]">
              <input type="checkbox" checked={form.enabled !== false} onChange={(e) => upd("enabled", e.target.checked)} /> Aktiv (Spot läuft im Vorspann)
            </label>
          </div>

          <div className="space-y-3">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#A8A398]">Live-Vorschau</div>
            <FullSlidePreview item={form} />
            <div className="text-[10px] text-[#575148]">Angezeigt {form.duration_sec} s zwischen zwei Trailern. Wird global im Kino-Vorspann rotiert (falls &bdquo;aktiv&ldquo;).</div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[#1A1A1A] flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[#A8A398]">Abbrechen</button>
          <button data-testid="ad-save-btn" onClick={save} disabled={busy} className="bg-[#D4AF37] text-black px-6 py-2 rounded-md text-sm uppercase tracking-widest hover:bg-[#EEDC82] flex items-center gap-2">
            <Save size={14} /> {busy ? "Speichern …" : "Speichern"}
          </button>
        </div>
      </div>
    </div>
  );
}

function FullPreview({ item, onClose }) {
  return (
    <div className="fixed inset-0 z-[60] bg-black/95 backdrop-blur flex items-center justify-center" onClick={onClose}>
      <div className="w-full max-w-4xl aspect-video relative">
        <FullSlidePreview item={item} />
        <button onClick={onClose} className="absolute top-4 right-4 text-[#F5F0E6]/70 hover:text-[#D4AF37] bg-black/50 backdrop-blur px-3 py-2 rounded-full border border-white/10 text-xs">Schließen</button>
      </div>
    </div>
  );
}

function FullSlidePreview({ item }) {
  const icon = adIconFor(item.title);
  return (
    <div className="relative aspect-video rounded-md overflow-hidden border border-[#1A1A1A]" style={{ background: item.background_color || "#0A0A0A", color: "#F5F0E6" }}>
      {item.media_url && <img src={item.media_url} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} className="absolute inset-0 w-full h-full object-cover opacity-40" />}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
      <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-8">
        <div className="text-[10px] uppercase tracking-[0.5em] mb-4" style={{ color: item.accent_color || "#D4AF37" }}>· Werbung ·</div>
        <div className="mb-4" style={{ color: item.accent_color || "#D4AF37" }}>{icon}</div>
        <div className="font-heading text-3xl md:text-5xl leading-[0.95]" style={{ color: item.accent_color || "#D4AF37", textShadow: "0 0 30px rgba(212,175,55,0.35)" }}>
          {item.title || "Titel"}
        </div>
        {item.subtitle && <div className="mt-4 text-sm md:text-lg text-[#F5F0E6]/85 max-w-2xl">{item.subtitle}</div>}
        {item.cta && (
          <div className="mt-6 inline-block border-2 rounded-full px-6 py-2 text-xs uppercase tracking-[0.4em]" style={{ borderColor: item.accent_color || "#D4AF37", color: item.accent_color || "#D4AF37" }}>
            {item.cta}
          </div>
        )}
      </div>
    </div>
  );
}

function adIconFor(title = "") {
  const t = title.toLowerCase();
  if (t.includes("snack") || t.includes("popcorn")) return <Popcorn size={40} />;
  if (t.includes("club") || t.includes("mitglied")) return <Sparkles size={40} />;
  if (t.includes("newsletter") || t.includes("mail")) return <Mail size={40} />;
  return <Sparkles size={40} />;
}
